import { useEffect, useRef } from 'react'
import * as styles from './activity-feed.css.ts'
import { ActivityProgress } from './activity-progress.tsx'
import { ActivityStepCardSkeleton } from './activity-step-card-skeleton.tsx'
import { ActivityStepCard, type ActivityStepCardProps } from './activity-step-card.tsx'

const INITIALIZING_STEP: ActivityStepCardProps = {
  title: 'Initialize',
  description: 'Initializing analysis and gathering code context',
  status: 'in_progress',
}
const INITIALIZING_TOTAL = 6
const INITIALIZING_SKELETON_COUNT = 5

export type ActivityFeedProps = {
  steps: ActivityStepCardProps[]
  ariaLabel?: string
  progressLabel?: string
  isComplete?: boolean
  overlay?: React.ReactNode
  completedEvents?: number
  totalEvents?: number
  isLive: boolean
}

export const ActivityFeed: React.FC<ActivityFeedProps> = ({
  steps,
  ariaLabel = 'Analysis activity feed',
  progressLabel,
  isComplete = false,
  overlay,
  completedEvents,
  totalEvents,
  isLive,
}) => {
  const scrollAnchorRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (isLive && steps.length > 0 && scrollAnchorRef.current) {
      scrollAnchorRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' })
    }
  }, [steps.length, isLive])

  const isInitializing = steps.length === 0 && !isComplete && !!progressLabel
  const displaySteps = isInitializing ? [INITIALIZING_STEP] : steps

  const completedSteps = isInitializing
    ? 0
    : (completedEvents ?? (isComplete ? steps.length : Math.max(0, steps.length - 1)))
  const totalSteps = isInitializing
    ? INITIALIZING_TOTAL
    : (totalEvents ?? (isComplete ? steps.length : steps.length + 1))
  const skeletonCount = isInitializing ? INITIALIZING_SKELETON_COUNT : isComplete ? 0 : totalSteps - completedSteps - 1

  return (
    <div className={styles.container} role="list" aria-label={ariaLabel}>
      {progressLabel && (
        <div className={styles.progressSection}>
          <ActivityProgress
            label={progressLabel}
            status={isInitializing ? 'initializing' : isComplete ? 'complete' : 'in-progress'}
            current={completedSteps}
            total={totalSteps}
          />
        </div>
      )}

      {displaySteps.length > 0 && (
        <div className={styles.stepsSection} style={{ position: 'relative' }}>
          {displaySteps.map((step, index) => (
            <ActivityStepCard key={`${step.title}-${index}`} {...step} />
          ))}
          {Array.from({ length: skeletonCount }, (_, index) => (
            <ActivityStepCardSkeleton key={`skeleton-${index}`} variant={index === 0 ? 'on-deck' : 'default'} />
          ))}
          {overlay}
          <div ref={scrollAnchorRef} aria-hidden="true" />
        </div>
      )}
    </div>
  )
}
